import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  title: "", slug: "", kind: "course", tagline: "", description: "",
  duration: "", fee: "", fee_note: "", image: "",
  includesText: "", modules: [], published: true, order: 0,
};

const inputCls =
  "w-full bg-white border border-black/20 focus:border-lima-blue outline-none text-lima-ink text-sm px-3 py-2.5 rounded-none placeholder:text-black/30 transition-colors duration-200";
const labelCls = "block font-mono2 text-[11px] tracking-[0.25em] text-black/50 mb-1.5";

function slugify(t) {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function courseToForm(course) {
  if (!course) return { ...EMPTY };
  return {
    title: course.title || "",
    slug: course.slug || "",
    kind: course.kind || "course",
    tagline: course.tagline || "",
    description: course.description || "",
    duration: course.duration || "",
    fee: course.fee || "",
    fee_note: course.fee_note || "",
    image: course.image || "",
    includesText: (course.includes || []).join("\n"),
    modules: (course.modules || []).map((m) => ({ title: m.title, topicsText: (m.topics || []).join(", ") })),
    published: course.published !== false,
    order: course.order || 0,
  };
}

export default function CourseForm({ open, onOpenChange, course, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(courseToForm(course));
  }, [open, course]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const setModule = (i, k, v) =>
    setForm((f) => {
      const modules = [...f.modules];
      modules[i] = { ...modules[i], [k]: v };
      return { ...f, modules };
    });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      slug: form.slug || slugify(form.title),
      kind: form.kind,
      tagline: form.tagline,
      description: form.description,
      duration: form.duration,
      fee: form.fee,
      fee_note: form.fee_note,
      image: form.image,
      includes: form.includesText.split("\n").map((s) => s.trim()).filter(Boolean),
      modules: form.modules
        .filter((m) => m.title.trim())
        .map((m) => ({ title: m.title.trim(), topics: m.topicsText.split(",").map((s) => s.trim()).filter(Boolean) })),
      published: form.published,
      order: Number(form.order) || 0,
    };
    try {
      if (course?.id) {
        await api.put(`/admin/courses/${course.id}`, payload);
        toast.success("Program updated.");
      } else {
        await api.post("/admin/courses", payload);
        toast.success("Program created.");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none border-black/20" data-testid="course-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight">
            {course?.id ? "Edit Program" : "New Program"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>TITLE *</label>
              <input required className={inputCls} data-testid="course-form-title" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, slug: course?.id ? f.slug : slugify(e.target.value) }))} />
            </div>
            <div>
              <label className={labelCls}>URL SLUG *</label>
              <input required className={inputCls} data-testid="course-form-slug" value={form.slug} onChange={set("slug")} />
              <p className="mt-1 text-[11px] text-black/40">Page lives at /programs/{form.slug || "…"}</p>
            </div>
            <div>
              <label className={labelCls}>TYPE</label>
              <select className={inputCls} data-testid="course-form-kind" value={form.kind} onChange={set("kind")}>
                <option value="course">Course (long-term)</option>
                <option value="workshop">Workshop (short-term)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>TAGLINE</label>
              <input className={inputCls} data-testid="course-form-tagline" value={form.tagline} onChange={set("tagline")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>DESCRIPTION</label>
              <textarea rows={4} className={`${inputCls} resize-none`} data-testid="course-form-description" value={form.description} onChange={set("description")} />
            </div>
            <div>
              <label className={labelCls}>DURATION</label>
              <input className={inputCls} data-testid="course-form-duration" placeholder="e.g. 6 months" value={form.duration} onChange={set("duration")} />
            </div>
            <div>
              <label className={labelCls}>DISPLAY ORDER</label>
              <input type="number" className={inputCls} data-testid="course-form-order" value={form.order} onChange={set("order")} />
            </div>
            <div>
              <label className={labelCls}>FEE</label>
              <input className={inputCls} data-testid="course-form-fee" placeholder="e.g. ₹85,000 or Fee on request" value={form.fee} onChange={set("fee")} />
            </div>
            <div>
              <label className={labelCls}>FEE NOTE</label>
              <input className={inputCls} data-testid="course-form-fee-note" placeholder="e.g. EMI available" value={form.fee_note} onChange={set("fee_note")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>IMAGE URL</label>
              <input className={inputCls} data-testid="course-form-image" placeholder="https://…" value={form.image} onChange={set("image")} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>WHAT'S INCLUDED — ONE PER LINE</label>
              <textarea rows={5} className={`${inputCls} resize-none font-mono2 text-xs`} data-testid="course-form-includes" value={form.includesText} onChange={set("includesText")} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={`${labelCls} mb-0`}>CURRICULUM MODULES</label>
              <button type="button" data-testid="course-form-add-module"
                onClick={() => setForm((f) => ({ ...f, modules: [...f.modules, { title: "", topicsText: "" }] }))}
                className="flex items-center gap-1.5 text-xs font-semibold text-lima-blue hover:text-lima-bluehover">
                <Plus size={14} /> Add Module
              </button>
            </div>
            <div className="space-y-3">
              {form.modules.map((m, i) => (
                <div key={i} className="border border-black/15 p-4 space-y-3" data-testid={`course-form-module-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono2 text-xs text-black/40">{String(i + 1).padStart(2, "0")}</span>
                    <input className={inputCls} placeholder="Module title" value={m.title}
                      data-testid={`course-form-module-title-${i}`}
                      onChange={(e) => setModule(i, "title", e.target.value)} />
                    <button type="button" aria-label="Remove module"
                      data-testid={`course-form-module-remove-${i}`}
                      onClick={() => setForm((f) => ({ ...f, modules: f.modules.filter((_, j) => j !== i) }))}
                      className="text-black/40 hover:text-lima-red shrink-0">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <input className={inputCls} placeholder="Topics, comma separated" value={m.topicsText}
                    data-testid={`course-form-module-topics-${i}`}
                    onChange={(e) => setModule(i, "topicsText", e.target.value)} />
                </div>
              ))}
              {form.modules.length === 0 && (
                <p className="text-xs text-black/40 font-mono2">No modules yet — add the curriculum breakdown.</p>
              )}
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={form.published} data-testid="course-form-published"
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              className="w-4 h-4 accent-[#0000EE]" />
            <span className="text-sm font-medium">Published (visible on the website)</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="course-form-cancel"
              className="border border-black/20 px-6 py-3 text-sm font-semibold hover:bg-black/5 rounded-none flex items-center gap-2">
              <X size={14} /> Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="course-form-save"
              className="bg-lima-blue hover:bg-lima-bluehover disabled:opacity-50 text-white px-8 py-3 text-sm font-semibold rounded-none transition-colors duration-200">
              {saving ? "SAVING…" : course?.id ? "Save Changes" : "Create Program"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
